import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/posthog";

// Site domain for redirects - use the actual deployed domain with fallback
const SITE_URL = import.meta.env.VITE_APP_URL || window.location.origin;

type AuthMode = "signin" | "signup" | "forgot-password";

interface AuthFormProps {
  returnTo?: string;
}

export default function AuthForm({ returnTo = '/dashboard' }: AuthFormProps) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<AuthMode>("signin");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signIn, signUp } = useAuth();

  const handleMagicLinkSignIn = async () => {
    if (!email || !email.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }

    setMagicLinkLoading(true);

    try {
      console.log("Attempting magic link sign in for:", email);
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${SITE_URL}/auth?returnTo=${encodeURIComponent(returnTo)}`,
        },
      });

      if (error) {
        console.error("Magic link error:", error);
        throw error;
      }

      toast.success("Magic link sent! Check your email to sign in.");
      console.log("Magic link sent successfully");
    } catch (error: any) {
      console.error("Magic link sign in error:", error);
      toast.error(error.message || "Failed to send magic link");
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "signin") {
        console.log("Attempting sign in with email:", email);
        trackEvent('sign_in_attempted', {
          method: 'email'
        });
        await signIn(email, password);
        trackEvent('sign_in_success', {
          method: 'email'
        });
        console.log("Sign in successful");
      } else if (mode === "signup") {
        // Validate emails match before signup
        if (email !== confirmEmail) {
          toast.error("Emails do not match");
          setLoading(false);
          return;
        }
        
        // Validate passwords match before signup
        if (password !== confirmPassword) {
          toast.error("Passwords do not match");
          setLoading(false);
          return;
        }

        console.log("Attempting sign up with email:", email);
        trackEvent('sign_up_attempted', {
          method: 'email'
        });
        await signUp(email, password);
        trackEvent('sign_up_success', {
          method: 'email'
        });
        // Reset form after signup
        setEmail("");
        setConfirmEmail("");
        setPassword("");
        setConfirmPassword("");
        setMode("signin");
        toast.success("Account created! Check your email for confirmation.");
        console.log("Sign up successful");
      } else if (mode === "forgot-password") {
        console.log("Attempting password reset for:", email);
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${SITE_URL}/reset-password?returnTo=${encodeURIComponent(returnTo)}`,
        });
        
        if (error) throw error;
        
        toast.success("Password reset instructions sent to your email");
        setMode("signin");
        console.log("Password reset email sent");
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      toast.error(error.message || "An error occurred during authentication");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = (newMode: AuthMode) => {
    setMode(newMode);
    // Reset email and password fields when switching modes
    setConfirmEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl">
          {mode === "signin" ? "Sign In" : 
           mode === "signup" ? "Create an Account" : 
           "Reset Password"}
        </CardTitle>
        <CardDescription>
          {mode === "signin" 
            ? "Sign in to access your habits" 
            : mode === "signup"
            ? "Sign up to start tracking your daily habits"
            : "Enter your email to receive password reset instructions"}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {mode === "signup" && (
            <div className="space-y-2">
              <Label htmlFor="confirmEmail">Confirm Email</Label>
              <Input
                id="confirmEmail"
                type="email"
                placeholder="name@example.com"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                required
              />
            </div>
          )}
          
          {mode !== "forgot-password" && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required={mode === "signin" || mode === "signup"}
                  minLength={mode === "signin" || mode === "signup" ? 6 : undefined}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>
          )}
          
          {mode === "signup" && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading
              ? "Loading..."
              : mode === "signin"
              ? "Sign In"
              : mode === "signup"
              ? "Create Account"
              : "Send Reset Instructions"}
          </Button>

          {mode === "signin" && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>
          )}

          {mode === "signin" && (
            <Button 
              type="button" 
              variant="outline" 
              className="w-full" 
              onClick={handleMagicLinkSignIn}
              disabled={magicLinkLoading || loading}
            >
              {magicLinkLoading ? "Sending..." : "Sign in with Magic Link"}
            </Button>
          )}
          
          <div className="flex flex-col gap-2 items-center text-sm text-muted-foreground">
            {mode === "signin" ? (
              <>
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => toggleMode("forgot-password")}
                >
                  Forgot your password?
                </button>
                <div>
                  Don't have an account?{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => toggleMode("signup")}
                  >
                    Sign Up
                  </button>
                </div>
                <div className="text-xs text-muted-foreground/70">
                  Didn't receive confirmation email?{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => navigate("/resend-activation")}
                  >
                    Resend activation email
                  </button>
                </div>
              </>
            ) : mode === "signup" ? (
              <>
                <div>
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => toggleMode("signin")}
                  >
                    Sign In
                  </button>
                </div>
                <div className="text-xs text-muted-foreground/70">
                  Didn't receive confirmation email?{" "}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => navigate("/resend-activation")}
                  >
                    Resend activation email
                  </button>
                </div>
              </>
            ) : (
              <div>
                Remember your password?{" "}
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => toggleMode("signin")}
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
